import { useState, useEffect, useCallback } from 'react';
import Seo from '../../components/Seo';
import { StatusBadge, PageLoader, ErrorState, EmptyState, Field } from '../../components/ui';
import { activitiesApi, mediaApi } from '../../api/resources';
import { assetUrl } from '../../api/client';
import { useToast } from '../../context/ToastContext';
import { formatDate } from '../../lib/format';
import { kindForFile } from '../../lib/media';
import { detectVideoProvider, providerLabel } from '../../lib/videoProvider';
import RichTextEditor from '../../components/RichTextEditor';

const STATUS_TABS = ['', 'draft', 'pending', 'approved', 'published', 'rejected'];

const EMPTY_FORM = {
  id: null,
  title: '',
  summary: '',
  description: '',
  occurredOn: '',
  status: 'draft',
  gallery: [],
};

let keySeq = 0;
const nextKey = () => `g${Date.now()}-${keySeq++}`;

export default function AdminActivities() {
  const toast = useToast();
  const [status, setStatus] = useState('');
  const [state, setState] = useState({ loading: true, error: null, items: [] });
  const [form, setForm] = useState(EMPTY_FORM);
  const [galleryDirty, setGalleryDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [linkInput, setLinkInput] = useState('');
  const [linkError, setLinkError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(() => {
    setState((s) => ({ ...s, loading: true, error: null }));
    activitiesApi
      .list({ status: status || undefined, per_page: 100 })
      .then((res) => setState({ loading: false, error: null, items: res.items }))
      .catch((error) => setState({ loading: false, error, items: [] }));
  }, [status]);

  useEffect(() => {
    load();
  }, [load]);

  function update(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  function resetForm() {
    setForm(EMPTY_FORM);
    setGalleryDirty(false);
    setLinkInput('');
    setLinkError('');
  }

  async function editActivity(row) {
    // Fetch the full record (list items may not eager-load the gallery relation).
    let full = row;
    try {
      full = await activitiesApi.get(row.slug || row.id);
    } catch {
      full = row;
    }
    const gallery = (full.gallery || []).map((g) => {
      if (g.type === 'embed') {
        return {
          key: nextKey(),
          kind: 'embed',
          url: g.url,
          embed_url: g.embed_url,
          provider: g.provider,
          alt_text: g.alt_text || '',
          mediaId: null,
          error: '',
        };
      }
      return {
        key: nextKey(),
        kind: g.type === 'video' ? 'video' : 'image',
        url: g.url,
        embed_url: null,
        provider: null,
        alt_text: g.alt_text || '',
        mediaId: g.media_id ?? null,
        error: '',
      };
    });
    setForm({
      id: full.id,
      title: full.title || '',
      summary: full.summary || '',
      description: full.description || '',
      occurredOn: full.occurred_on || '',
      status: full.status || 'draft',
      gallery,
    });
    setGalleryDirty(false);
    setLinkInput('');
    setLinkError('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  async function handleUpload(e) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const kind = kindForFile(file); // 'image' | 'video' | 'document'
        if (kind === 'document') {
          toast.error(`${file.name}: only images and videos can be added to the gallery.`);
          continue;
        }
        const fd = new FormData();
        fd.append('file', file);
        fd.append('kind', kind);
        fd.append('alt_text', file.name);
        const media = await mediaApi.upload(fd);
        // Read from `media.url` / `media.id` (never `media.path`).
        if (media?.id == null) throw new Error('Upload succeeded but no media id was returned.');
        setForm((f) => ({
          ...f,
          gallery: [
            ...f.gallery,
            {
              key: nextKey(),
              kind,
              url: media.url,
              embed_url: null,
              provider: null,
              alt_text: media.alt_text || file.name,
              mediaId: media.id,
              error: '',
            },
          ],
        }));
        setGalleryDirty(true);
      }
      toast.success(files.length > 1 ? 'Media uploaded.' : 'Media uploaded.');
    } catch (err) {
      toast.error(err.message || 'Upload failed.');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  }

  function addVideoLink() {
    const url = linkInput.trim();
    if (!url) return;
    const provider = detectVideoProvider(url);
    if (!provider) {
      setLinkError('Only YouTube or Facebook video links, or public Facebook post links, are allowed.');
      return;
    }
    setForm((f) => ({
      ...f,
      gallery: [
        ...f.gallery,
        {
          key: nextKey(),
          kind: 'embed',
          url,
          embed_url: null,
          provider,
          alt_text: '',
          mediaId: null,
          error: '',
        },
      ],
    }));
    setGalleryDirty(true);
    setLinkInput('');
    setLinkError('');
  }

  function removeItem(key) {
    setForm((f) => ({ ...f, gallery: f.gallery.filter((g) => g.key !== key) }));
    setGalleryDirty(true);
  }

  function moveItem(index, delta) {
    setForm((f) => {
      const next = [...f.gallery];
      const target = index + delta;
      if (target < 0 || target >= next.length) return f;
      [next[index], next[target]] = [next[target], next[index]];
      return { ...f, gallery: next };
    });
    setGalleryDirty(true);
  }

  /** Build the `gallery` payload array, or throw if an upload can't be resolved. */
  function buildGalleryPayload() {
    return form.gallery.map((g, i) => {
      if (g.kind === 'embed') return { url: g.url };
      if (g.mediaId == null) {
        throw new Error(`Gallery item ${i + 1} is missing its media reference. Remove and re-add it, then save.`);
      }
      return { media_id: g.mediaId };
    });
  }

  /** Map a Laravel 422 gallery.* error bag onto the form items by index. */
  function applyGalleryErrors(errors) {
    if (!errors) return false;
    let matched = false;
    setForm((f) => {
      const next = f.gallery.map((g) => ({ ...g, error: '' }));
      Object.entries(errors).forEach(([key, msgs]) => {
        const m = /^gallery\.(\d+)/.exec(key);
        if (m) {
          const idx = Number(m[1]);
          if (next[idx]) {
            next[idx].error = Array.isArray(msgs) ? msgs[0] : String(msgs);
            matched = true;
          }
        }
      });
      return { ...f, gallery: next };
    });
    return matched;
  }

  function buildBasePayload() {
    return {
      title: form.title.trim(),
      summary: form.summary.trim() || null,
      description: form.description || null,
      occurred_on: form.occurredOn || null,
    };
  }

  /** Persist create/update. Returns { id, status } or null on handled failure. */
  async function persist() {
    if (!form.title.trim()) {
      toast.error('Title is required.');
      return null;
    }
    const payload = buildBasePayload();
    if (galleryDirty) {
      try {
        payload.gallery = buildGalleryPayload(); // may be [] to clear
      } catch (err) {
        toast.error(err.message);
        return null;
      }
    }
    try {
      const saved = form.id
        ? await activitiesApi.update(form.id, payload)
        : await activitiesApi.create(payload);
      return { id: saved?.id ?? form.id, status: saved?.status ?? form.status ?? 'draft' };
    } catch (err) {
      if (err.status === 422 && applyGalleryErrors(err.errors)) {
        toast.error('Please fix the highlighted gallery items.');
      } else {
        toast.error(err.message || 'Could not save activity.');
      }
      return null;
    }
  }

  /** Run the approval state machine up to "published" from the current status. */
  async function transitionToPublished(id, currentStatus) {
    const order = ['draft', 'pending', 'approved', 'published'];
    let idx = order.indexOf(currentStatus || 'draft');
    if (idx === -1 || currentStatus === 'rejected') idx = 0;
    const steps = [];
    if (idx < 1) steps.push('submit');
    if (idx < 2) steps.push('approve');
    if (idx < 3) steps.push('publish');
    for (const step of steps) {
      await activitiesApi[step](id, 'Published via Admin Activities manager.');
    }
  }

  async function handleSaveDraft() {
    setBusy(true);
    try {
      const res = await persist();
      if (!res) return;
      toast.success(form.id ? 'Activity updated.' : 'Draft saved.');
      resetForm();
      load();
    } finally {
      setBusy(false);
    }
  }

  async function handleSavePublish() {
    setBusy(true);
    try {
      const res = await persist();
      if (!res) return;
      if (res.status !== 'published') {
        await transitionToPublished(res.id, res.status);
        toast.success('Activity saved and published.');
      } else {
        toast.success('Activity saved (already published).');
      }
      resetForm();
      load();
    } catch (err) {
      toast.error(err.message || 'Could not publish activity.');
    } finally {
      setBusy(false);
    }
  }

  async function handlePublishRow(row) {
    setBusy(true);
    try {
      await transitionToPublished(row.id, row.status);
      toast.success('Activity published.');
      load();
    } catch (err) {
      toast.error(err.message || 'Could not publish activity.');
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete(row) {
    setBusy(true);
    try {
      await activitiesApi.remove(row.id);
      toast.success('Activity deleted.');
      setConfirmDelete(null);
      if (form.id === row.id) resetForm();
      load();
    } catch (err) {
      toast.error(err.message || 'Could not delete activity.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-6">
      <Seo title="Manage Activities" />
      <div className="flex items-center justify-between">
        <h1 className="font-display text-2xl font-bold">Activities</h1>
        {form.id && (
          <button onClick={resetForm} className="btn-ghost text-sm" disabled={busy}>
            + New activity
          </button>
        )}
      </div>

      {/* Create / edit panel */}
      <div className="card space-y-5 p-6">
        <h2 className="font-display text-lg font-semibold">
          {form.id ? 'Edit activity' : 'New activity'}
        </h2>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Field label="Title *">
              <input
                className="input"
                value={form.title}
                onChange={(e) => update('title', e.target.value)}
                placeholder="Activity title"
              />
            </Field>
          </div>

          <Field label="Date" hint="When the activity took place.">
            <input
              type="date"
              className="input"
              value={form.occurredOn}
              onChange={(e) => update('occurredOn', e.target.value)}
            />
          </Field>

          <div className="sm:col-span-2">
            <Field label="Summary" hint="Short one-line teaser (optional).">
              <input
                className="input"
                value={form.summary}
                onChange={(e) => update('summary', e.target.value)}
                placeholder="Brief summary"
              />
            </Field>
          </div>

          <div className="sm:col-span-2">
            <Field label="Description" hint="Format text, add links, insert images, and build tables. Sanitized on save.">
              <RichTextEditor
                id="activity-description"
                value={form.description}
                onChange={(html) => update('description', html)}
                placeholder="What happened during this activity?"
              />
            </Field>
          </div>
        </div>

        {/* Gallery manager */}
        <div className="space-y-4 border-t border-cream-300 pt-5">
          <div>
            <h3 className="font-display text-base font-semibold text-ink">Gallery</h3>
            <p className="text-sm text-ink-muted">
              Upload images and videos, or add a YouTube / Facebook video link. Drag order with the arrows.
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Upload photos / videos" hint="Images and MP4 / WebM video.">
              <input
                type="file"
                accept="image/*,video/mp4,video/webm"
                multiple
                onChange={handleUpload}
                className="text-sm"
                disabled={uploading}
              />
              {uploading && <p className="mt-1 text-xs text-ink-muted">Uploading…</p>}
            </Field>

            <Field label="Add video or Facebook post link" error={linkError} hint="YouTube or Facebook video URL, or a public Facebook post URL.">
              <div className="flex gap-2">
                <input
                  className="input"
                  value={linkInput}
                  onChange={(e) => {
                    setLinkInput(e.target.value);
                    if (linkError) setLinkError('');
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addVideoLink();
                    }
                  }}
                  placeholder="https://www.youtube.com/watch?v=…"
                />
                <button type="button" onClick={addVideoLink} className="btn-outline shrink-0">
                  Add
                </button>
              </div>
            </Field>
          </div>

          {form.gallery.length > 0 && (
            <ul className="grid gap-3 sm:grid-cols-2">
              {form.gallery.map((g, i) => (
                <li
                  key={g.key}
                  className={`flex items-center gap-3 rounded-xl border bg-surface-alt p-2 ${
                    g.error ? 'border-red-400' : 'border-cream-300'
                  }`}
                >
                  <GalleryThumb item={g} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-ink-soft">
                      {g.kind === 'embed'
                        ? providerLabel(g.provider)
                        : g.kind === 'video'
                          ? 'Video'
                          : g.alt_text || `Photo ${i + 1}`}
                    </p>
                    {g.kind === 'embed' && (
                      <p className="truncate text-xs text-ink-muted">{g.url}</p>
                    )}
                    {g.error && <p className="mt-0.5 text-xs text-red-600">{g.error}</p>}
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <button
                      type="button"
                      onClick={() => moveItem(i, -1)}
                      disabled={i === 0}
                      aria-label="Move up"
                      className="btn-ghost px-2 py-1 text-xs disabled:opacity-40"
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      onClick={() => moveItem(i, 1)}
                      disabled={i === form.gallery.length - 1}
                      aria-label="Move down"
                      className="btn-ghost px-2 py-1 text-xs disabled:opacity-40"
                    >
                      ↓
                    </button>
                    <button
                      type="button"
                      onClick={() => removeItem(g.key)}
                      aria-label="Remove item"
                      className="btn-ghost px-2 py-1 text-xs text-red-600"
                    >
                      Remove
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-3 border-t border-cream-300 pt-5">
          <button onClick={handleSavePublish} className="btn-primary" disabled={busy || uploading}>
            {busy ? 'Working…' : 'Save & Publish'}
          </button>
          <button onClick={handleSaveDraft} className="btn-outline" disabled={busy || uploading}>
            Save as Draft
          </button>
          {form.id && (
            <button onClick={resetForm} className="btn-ghost ml-auto" disabled={busy}>
              Cancel
            </button>
          )}
        </div>
      </div>

      {/* Status filter */}
      <div className="flex flex-wrap gap-2">
        {STATUS_TABS.map((t) => (
          <button
            key={t || 'all'}
            onClick={() => setStatus(t)}
            className={`pill cursor-pointer capitalize transition-colors ${
              status === t ? 'bg-maroon-700 text-white' : 'bg-surface-gray text-ink-soft hover:bg-cream-300'
            }`}
          >
            {t || 'All'}
          </button>
        ))}
      </div>

      {/* Activities list */}
      {state.loading ? (
        <PageLoader />
      ) : state.error ? (
        <ErrorState error={state.error} onRetry={load} />
      ) : state.items.length === 0 ? (
        <EmptyState title="No activities in this view" message="Create your first activity using the form above." />
      ) : (
        <div className="card overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-cream-300 bg-surface-alt text-xs uppercase tracking-wider text-ink-soft">
              <tr>
                <th className="px-5 py-3">Title</th>
                <th className="px-5 py-3">Date</th>
                <th className="px-5 py-3">Gallery</th>
                <th className="px-5 py-3">Status</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-cream-300">
              {state.items.map((a) => (
                <tr key={a.id} className="hover:bg-surface-alt">
                  <td className="px-5 py-3 font-medium text-ink">{a.title}</td>
                  <td className="px-5 py-3 text-ink-muted">
                    {a.occurred_on ? formatDate(a.occurred_on) : '—'}
                  </td>
                  <td className="px-5 py-3 text-ink-muted">{a.gallery?.length ?? 0}</td>
                  <td className="px-5 py-3">
                    <StatusBadge status={a.status} />
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex flex-wrap justify-end gap-2">
                      <a
                        href={`/activities/${a.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-ghost px-3 py-1.5 text-xs"
                      >
                        View
                      </a>
                      <button onClick={() => editActivity(a)} className="btn-outline px-3 py-1.5 text-xs">
                        Edit
                      </button>
                      {a.status !== 'published' && (
                        <button
                          onClick={() => handlePublishRow(a)}
                          className="btn-primary px-3 py-1.5 text-xs"
                          disabled={busy}
                        >
                          Publish
                        </button>
                      )}
                      <button
                        onClick={() => setConfirmDelete(a)}
                        className="btn-ghost px-3 py-1.5 text-xs text-red-600"
                        disabled={busy}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {confirmDelete && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-maroon-950/60 p-4"
          onClick={() => setConfirmDelete(null)}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="w-full max-w-md rounded-2xl bg-surface-card p-6 shadow-lift"
            onClick={(ev) => ev.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-ink">Delete activity?</h3>
            <p className="mt-2 text-sm text-ink-muted">
              “{confirmDelete.title}” will be removed. Attached media stays in the library. This cannot be undone.
            </p>
            <div className="mt-6 flex justify-end gap-2">
              <button onClick={() => setConfirmDelete(null)} className="btn-ghost" disabled={busy}>
                Cancel
              </button>
              <button onClick={() => handleDelete(confirmDelete)} className="btn-danger" disabled={busy}>
                {busy ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function GalleryThumb({ item }) {
  if (item.kind === 'image') {
    return (
      <img
        src={assetUrl(item.url)}
        alt={item.alt_text || ''}
        className="h-14 w-14 shrink-0 rounded-lg object-cover"
      />
    );
  }
  if (item.kind === 'video') {
    return (
      <div className="relative h-14 w-14 shrink-0 overflow-hidden rounded-lg bg-black">
        <video src={assetUrl(item.url)} muted preload="metadata" className="h-full w-full object-cover" />
        <span className="absolute inset-0 flex items-center justify-center text-white">▶</span>
      </div>
    );
  }
  return (
    <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-maroon-800 to-maroon-950 text-lg text-cream-100">
      ▶
    </div>
  );
}
